import i18n, { StringMap, TOptionsBase } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

import configs from '@webtrader/core/configs';
import { EProjectPlatform } from '@webtrader/core/configs/types';
import nativeCookies, { ENativeCookieName } from '@webtrader/core/cookies/native';
import dayjs from '@webtrader/core/dayjs';
import storage, { EStorageKey } from '@webtrader/core/storage';
import { broadcastLanguageChangeMessage } from '@webtrader/core/syncing/language';
import { checkIsNative } from '@webtrader/core/validators/device';

import arAEResource from '../../i18n/locales/ar_AE/resource.json';
import enGBResource from '../../i18n/locales/en_GB/resource.json';
import esESResource from '../../i18n/locales/es_ES/resource.json';
import idIDResource from '../../i18n/locales/id_ID/resource.json';
import jaJPResource from '../../i18n/locales/ja_JP/resource.json';
import koKRResource from '../../i18n/locales/ko_KR/resource.json';
import msMYResource from '../../i18n/locales/ms_MY/resource.json';
import ptBRResource from '../../i18n/locales/pt_BR/resource.json';
import thTHResource from '../../i18n/locales/th_TH/resource.json';
import viVNResource from '../../i18n/locales/vi_VN/resource.json';
import zhCNResource from '../../i18n/locales/zh_CN/resource.json';
import zhTWResource from '../../i18n/locales/zh_TW/resource.json';
import cookies from '../cookies';
import { overloadTranslationOptionHandler, resolveMultipleTranslation } from './plugins';

export type TLang =
  | 'en_GB'
  | 'zh_CN'
  | 'zh_TW'
  | 'th_TH'
  | 'vi_VN'
  | 'ms_MY'
  | 'id_ID'
  | 'ja_JP'
  | 'ko_KR'
  | 'ar_AE'
  | 'es_ES'
  | 'pt_BR';
type TTranslationOptions = TOptionsBase & StringMap;

export interface ILangOption {
  value: TLang;
  label: string;
}

export const LANG_OPTIONS_MAP: {
  [key in TLang]: ILangOption;
} = {
  en_GB: {
    value: 'en_GB',
    label: 'English'
  },
  zh_CN: {
    value: 'zh_CN',
    label: '简体中文'
  },
  zh_TW: {
    value: 'zh_TW',
    label: '繁体中文'
  },
  th_TH: {
    value: 'th_TH',
    label: 'ภาษาไทย'
  },
  vi_VN: {
    value: 'vi_VN',
    label: 'Tiếng Việt'
  },
  ms_MY: {
    value: 'ms_MY',
    label: 'Melayu'
  },
  id_ID: {
    value: 'id_ID',
    label: 'Bahasa Indonesia'
  },
  ja_JP: {
    value: 'ja_JP',
    label: 'Japanese'
  },
  ko_KR: {
    value: 'ko_KR',
    label: 'Korean'
  },
  ar_AE: {
    value: 'ar_AE',
    label: 'Arabic'
  },
  es_ES: {
    value: 'es_ES',
    label: 'Español'
  },
  pt_BR: {
    value: 'pt_BR',
    label: 'Português'
  }
};

export const LANG_OPTIONS: ILangOption[] = Object.values(LANG_OPTIONS_MAP);

export const FALLBACK_LANG_OPTION: ILangOption = LANG_OPTIONS[0];

const retrieveResourceBundle = (lang: TLang) => {
  switch (lang) {
    case 'id_ID':
      return idIDResource;
    case 'ja_JP':
      return jaJPResource;
    case 'ko_KR':
      return koKRResource;
    case 'ms_MY':
      return msMYResource;
    case 'th_TH':
      return thTHResource;
    case 'vi_VN':
      return viVNResource;
    case 'zh_CN':
      return zhCNResource;
    case 'zh_TW':
      return zhTWResource;
    case 'ar_AE':
      return arAEResource;
    case 'es_ES':
      return esESResource;
    case 'pt_BR':
      return ptBRResource;
    default:
      return enGBResource;
  }
};

export const getLanguageFromStorage = () => {
  return checkIsNative() ? storage.getItem(EStorageKey.LANGUAGE) : cookies.get('lang');
};

const retrieveCurrentLanguageOption = () => {
  const locale = getLanguageFromStorage() || '';

  if (locale) {
    // Make sure locale is not empty string, else value.startsWith('') will choose first option
    return LANG_OPTIONS.find(({ value }) => value.startsWith(locale));
  }
  return undefined;
};

export const getUserLanguage = () => {
  return retrieveCurrentLanguageOption()?.value;
};

export let lang: TLang = getUserLanguage() || FALLBACK_LANG_OPTION.value;

export let initialLanguageChanged = false;

export const isRTL = () => {
  return getUserLanguage() === 'ar_AE';
};

// Callback to restart the app / reload the page upon language change
export let languageChangeRestartCallback: () => void = () => {};

export const setLanguageChangeRestartCallback = (callback: () => void) => {
  languageChangeRestartCallback = callback;
};

export const setCachedLanguage = (language: TLang) => {
  lang = language;
};

export const setInitialLanguageChanged = (changed: boolean) => {
  initialLanguageChanged = changed;
};

export const mapLangToLabel = (lang: TLang) => {
  return LANG_OPTIONS_MAP[lang]?.label;
};

export const getLanguageLabel = (): string => {
  return mapLangToLabel(i18n.language as TLang) ?? FALLBACK_LANG_OPTION.label;
};

export const getLanguage = (): TLang => {
  return retrieveCurrentLanguageOption()?.value ?? FALLBACK_LANG_OPTION.value;
};

export const saveLanguagePreference = (language: TLang) => {
  if (checkIsNative()) {
    storage.setItem(EStorageKey.LANGUAGE, language);
    nativeCookies.setValue(ENativeCookieName.LANG, language);
  } else {
    broadcastLanguageChangeMessage({ lang: language });
    cookies.set('lang', language, { path: '/' });
  }
};

export const changeLanguage = async (param: {
  language: TLang;
  shouldStore?: boolean;
  updateLanguageCallback?: () => Promise<void>; // Callback to update language via API call
}) => {
  const { language, shouldStore = true, updateLanguageCallback } = param;
  i18n.addResourceBundle(language, 'resource', retrieveResourceBundle(language));
  // Get the reference of the current language
  const currentLang = lang;

  // Lock RTL state during language transition to prevent race conditions
  const isRTLTransition =
    (currentLang !== 'ar_AE' && language === 'ar_AE') || (currentLang === 'ar_AE' && language !== 'ar_AE');

  await i18n.changeLanguage(language).then(async () => {
    // update the cached language
    setCachedLanguage(language);
    changeDayLocale(language);
    if (shouldStore) {
      saveLanguagePreference(language);
    }
    await updateLanguageCallback?.();

    // Only restart for RTL transitions after initial language has been set
    // Skip restart on initial language change to prevent crashes
    if (isRTLTransition && initialLanguageChanged && shouldStore) {
      // Restart callback is initialized during mobile and web application startup to prevent language change from being triggered multiple times
      languageChangeRestartCallback?.();
    }
  });
};

// eslint-disable-next-line complexity
export const changeDayLocale = (lang: TLang) => {
  switch (lang) {
    case 'en_GB':
      dayjs.locale('en');
      break;
    case 'zh_CN':
      dayjs.locale('zh-cn');
      break;
    case 'zh_TW':
      dayjs.locale('zh-tw');
      break;
    case 'th_TH':
      dayjs.locale('th');
      break;
    case 'vi_VN':
      dayjs.locale('vi');
      break;
    case 'ms_MY':
      dayjs.locale('ms-my');
      break;
    case 'id_ID':
      dayjs.locale('id');
      break;
    case 'ja_JP':
      dayjs.locale('ja');
      break;
    case 'ko_KR':
      dayjs.locale('ko');
      break;
    case 'ar_AE':
      dayjs.locale('ar');
      break;
    case 'es_ES':
      dayjs.locale('es');
      break;
    case 'pt_BR':
      dayjs.locale('pt-br');
      break;
  }
};

export const retrieveDayLocale = () => {
  switch (lang) {
    case 'zh_CN':
      return 'zh-cn';
    case 'zh_TW':
      return 'zh-tw';
    case 'th_TH':
      return 'th';
    case 'vi_VN':
      return 'vi';
    case 'ms_MY':
      return 'ms-my';
    case 'id_ID':
      return 'id';
    case 'ja_JP':
      return 'ja';
    case 'ko_KR':
      return 'ko';
    case 'ar_AE':
      return 'ar';
    case 'es_ES':
      return 'es';
    case 'pt_BR':
      return 'pt-br';
    default:
      return 'en';
  }
};

export const i18nText = (text: string, language?: string, params?: TTranslationOptions) => {
  if (!language) {
    return text;
  }

  if (params) {
    return i18n.t(text, params);
  }

  return i18n.t(text);
};

export const initialLanguageChange = (
  language: TLang | undefined | null,
  updateLanguageCallback?: () => Promise<void>
) => {
  if (initialLanguageChanged) {
    return;
  }
  setInitialLanguageChanged(true);
  if (i18n.isInitialized) {
    changeLanguage({
      language: getUserLanguage() || language || FALLBACK_LANG_OPTION.value,
      shouldStore: false,
      updateLanguageCallback
    });
  } else {
    i18n.on('initialized', async () => {
      changeLanguage({
        language: getUserLanguage() || language || FALLBACK_LANG_OPTION.value,
        shouldStore: false,
        updateLanguageCallback
      });
    });
  }
};

if (!i18n.isInitialized) {
  i18n
    .use(initReactI18next)
    .use(resolveMultipleTranslation)
    // .use(languageDetector)
    .init({
      load: 'languageOnly',

      fallbackLng: FALLBACK_LANG_OPTION.value,

      debug: configs.platform === EProjectPlatform.WEB ? NODE_ENV === 'development' : configs.environment === 'dev',

      defaultNS: 'resource',

      ns: ['resource'],

      // send not translated keys to endpoint
      saveMissing: false,

      // we do not use keys in form messages.welcome
      keySeparator: false,
      nsSeparator: false,

      interpolation: {
        // react already safes from xss
        escapeValue: false
      },

      // t function can return null, this behaviour is set by default, if you want to change it, set returnNull type to false.
      returnNull: false,

      overloadTranslationOptionHandler: overloadTranslationOptionHandler,

      resources: {
        en_GB: {},
        zh_CN: {},
        zh_TW: {},
        th_TH: {},
        vi_VN: {},
        ms_MY: {},
        id_ID: {},
        ja_JP: {},
        ko_KR: {},
        ar_AE: {},
        es_ES: {},
        pt_BR: {}
      }
    });
}

export default i18n;

export { useTranslation };
